import {
  ControlPosition,
  MapControl,
  useApiIsLoaded,
  useMap,
} from "@vis.gl/react-google-maps";
import { CSSProperties, useEffect, useRef, useState } from "react";
import {
  TerraDraw,
  TerraDrawLineStringMode,
  TerraDrawPolygonMode,
} from "terra-draw";
import { TerraDrawGoogleMapsAdapter } from "terra-draw-google-maps-adapter";
import GeoCoordinatesDTO from "../../../dtos/GeoCoordinatesDTO";
import { DrawingMode } from "./DrawingMode";

type ShapeTool = "polygon" | "linestring";

const toolButtonStyle = (active: boolean): CSSProperties => ({
  backgroundColor: active ? "#e6e6e6" : "#ffffff",
  border: "none",
  borderRadius: "2px",
  boxShadow: "rgba(0, 0, 0, 0.3) 0px 1px 4px -1px",
  color: "#000000",
  cursor: "pointer",
  fontSize: "14px",
  margin: "10px 2px 0 2px",
  padding: "8px 12px",
});

const DrawingManager = ({
  drawingMode,
  onDrawingFinish,
}: {
  drawingMode: DrawingMode;
  onDrawingFinish: (geoCoordinates: GeoCoordinatesDTO[]) => void;
}) => {
  const map = useMap();
  const apiIsLoaded = useApiIsLoaded();
  const drawRef = useRef<TerraDraw | null>(null);
  const onDrawingFinishRef = useRef(onDrawingFinish);
  const [activeTool, setActiveTool] = useState<ShapeTool>("polygon");

  useEffect(() => {
    onDrawingFinishRef.current = onDrawingFinish;
  }, [onDrawingFinish]);

  const drawingActive =
    drawingMode === DrawingMode.SHAPE ||
    drawingMode === DrawingMode.PROTECTION_ZONE;

  useEffect(() => {
    if (!apiIsLoaded) return;
    if (!map) return;
    if (!drawingActive) return;

    const draw = new TerraDraw({
      adapter: new TerraDrawGoogleMapsAdapter({
        lib: google.maps,
        map,
        coordinatePrecision: 9,
      }),
      modes: [
        new TerraDrawPolygonMode({
          styles: {
            outlineColor: "#000000",
            outlineWidth: 2,
            fillColor: "#000000",
            fillOpacity: 0.4,
          },
        }),
        new TerraDrawLineStringMode({
          styles: {
            lineStringColor: "#000000",
            lineStringWidth: 5,
          },
        }),
      ],
    });

    draw.on("ready", () => {
      draw.setMode("polygon");
    });

    draw.on("finish", (id, context) => {
      if (context.action !== "draw") return;

      const feature = draw.getSnapshotFeature(id);
      if (!feature) return;

      const coordinates: GeoCoordinatesDTO[] = [];
      const coordinatesType =
        drawingMode === DrawingMode.PROTECTION_ZONE ? 1 : 0;

      if (feature.geometry.type === "Polygon") {
        // GeoJSON polygon rings repeat the first coordinate at the end;
        // the backend expects an open ring, so drop the closing coordinate.
        const ring = feature.geometry.coordinates[0].slice(0, -1);
        ring.forEach(([longitude, latitude]: number[], idx: number) => {
          coordinates.push({
            waterType: 0,
            latitude,
            longitude,
            waterGroup: -1,
            groupPos: idx,
            coordinatesType,
          });
        });
      } else if (feature.geometry.type === "LineString") {
        feature.geometry.coordinates.forEach(([longitude, latitude]: number[], idx: number) => {
          coordinates.push({
            waterType: 1,
            latitude,
            longitude,
            waterGroup: -1,
            groupPos: idx,
            coordinatesType,
          });
        });
      } else {
        return;
      }

      onDrawingFinishRef.current(coordinates);
    });

    draw.start();
    drawRef.current = draw;
    setActiveTool("polygon");

    return () => {
      drawRef.current = null;
      draw.stop();
    };
  }, [map, apiIsLoaded, drawingActive, drawingMode]);

  const selectTool = (tool: ShapeTool) => {
    setActiveTool(tool);
    drawRef.current?.setMode(tool);
  };

  if (!drawingActive) {
    return <></>;
  }

  return (
    <MapControl position={ControlPosition.TOP_CENTER}>
      <button
        type="button"
        style={toolButtonStyle(activeTool === "polygon")}
        onClick={() => selectTool("polygon")}
      >
        Polygon
      </button>
      <button
        type="button"
        style={toolButtonStyle(activeTool === "linestring")}
        onClick={() => selectTool("linestring")}
      >
        Linie
      </button>
    </MapControl>
  );
};

export default DrawingManager;
